'use client';

import * as React from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { useToaster } from '@/components/hooks/useToaster';
import { useTimeout } from '@/components/hooks/useTimeout';
import ButtonIcon from '@/components/ui/ButtonIcon';
import Button, { IButtonProps } from '@/components/ui/Button';
import Icon from '@/components/ui/Icon';
import Buttons from '@/components/ui/Buttons';
import './styles.scss';

export interface IToastProps {
  id?: string;
  text?: string;
  description?: string;
  intent?: 'default' | 'warning' | 'danger' | 'success';
  buttons?: IButtonProps[];
}

const Toast: React.FunctionComponent<IToastProps> = (props) => {
  const { t } = useTranslation();
  const { closeToast } = useToaster();
  const [isOpen, setIsOpen] = React.useState(false);
  const [timerHiding, setTimerHiding] = React.useState<number | null>(null);
  useTimeout(() => {
    setTimerHiding(null);
    closeToast({ id: props.id });
  }, timerHiding);

  React.useEffect(() => {
    setIsOpen(true);
  }, []);

  return (
    <div
      className={classNames('toast', {
        ['is-open']: isOpen,
        ['is-closing']: !!timerHiding,

        ['intent-default']: props.intent === 'default' || !props.intent,
        ['intent-danger']: props.intent === 'danger',
        ['intent-warning']: props.intent === 'warning',
        ['intent-success']: props.intent === 'success',
      })}
    >
      <div className={'toast__wrapper'}>
        <div className={'toast__inner'}>
          <Icon
            width={24}
            height={24}
            kind="info"
            className={'toast__icon'}
          />
          <div className={'toast__text'}>
            <div className={'toast__text__title'}>{props.text}</div>
            {props.description && <div className={'toast__text__description'}>{props.description}</div>}
            {props.buttons && props.buttons.length > 0 && (
              <Buttons size="small">
                {props.buttons.map((b, bi) => (
                  <Button
                    key={bi}
                    size="small"
                    {...b}
                  />
                ))}
              </Buttons>
            )}
          </div>
          <div className={'toast__close'}>
            <ButtonIcon
              title={t('generic.actions.close')}
              icon="close"
              size="small"
              theme="transparent"
              type="button"
              onClick={(e) => {
                e.preventDefault();
                setTimerHiding(250);
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

Toast.displayName = 'Toast';

export default Toast;
